/* global window, tracking, _gaq, ga */
'use strict';
const get = require('lodash/get');
const isArray = require('lodash/isArray');
const isEmpty = require('lodash/isEmpty');
const merge = require('lodash/merge');
const debug = require('debug')('webstore:track');
const LocalStorage = require('public/js/lib/local-storage');

const Tracker = function(tracker_name, tracker_type, owner, is_ec) {
	this.name = tracker_name;
	this.type = tracker_type;
	this.owner = owner;
	this.is_ec = is_ec;
};

Tracker.prototype.get_tracker_prefix = function() {
	return (this.name === 'default') ? '' : this.name+'.';
};

Tracker.prototype.track_pageview = function (page) {
	if (this.type !== 'rp') {
		const tracker_prefix = this.get_tracker_prefix();

		// If enhanced ecommerce is enabled, then do enhanced ecommerce things
		if (this.is_ec) {
			this.do_ec_product_tracking();
		}

		// Make sure we are getting the most up-to-date URL in the location field since
		// sometimes we reload a significant part of the page asynchronously
		ga(`${tracker_prefix}set`, 'location', window.location.href);

		// If page is not set, don't send it over to avoid using `undefined` as the page
		if (page) {
			ga(`${tracker_prefix}send`, 'pageview', page);
		} else {
			ga(`${tracker_prefix}send`, 'pageview');
		}
	}
};

Tracker.prototype.track_event = function (event_args, options) {
	if (this.type === 'rp') { return; }

	const tracker_prefix = this.get_tracker_prefix();

	options = options || {};
	if(!options.eventCategory) {
		options.eventCategory = event_args[0];
	}
	if(!options.eventAction) {
		options.eventAction = event_args[1];
	}
	if(!options.eventLabel) {
		options.eventLabel = event_args[2];
	}

	ga(tracker_prefix+'send', 'event', options);
};

Tracker.prototype.track_transaction = function() {
	if(tracking.transaction) {
		switch(this.type) {
			case 'ua':
				this.ua_transaction();
				//this.ec_transaction();
				break;
			case 'rp':
				// TODO: RP conversion tracking
				break;
			case 'gaq':
				this.gaq_transaction();
				break;
		}
	}
};

/* Transactions
.*-----------------------------------------*/
Tracker.prototype.gaq_transaction = function() {
	var tracker_prefix, transaction, item, i, j;

	/* Set up the Tracker Prefix
	.*-----------------------------------------*/
	tracker_prefix = this.get_tracker_prefix();
	transaction = tracking.transaction;
	if(!transaction) { return; }

	/* Build Transaction
	.*-----------------------------------------*/
	_gaq.push([tracker_prefix+'_addTrans',
		transaction.order_num,   // order ID - required
		transaction.affiliation, // affiliation or store name
		transaction.revenue,     // total - required
		transaction.tax,         // tax
		transaction.shipping,    // shipping
		transaction.city,        // city
		transaction.region,      // state or province
		transaction.country      // country
	]);

	/* Loop through items and add them to the transaction
	.*-----------------------------------------*/
	for(i = 0, j = transaction.items.length; i < j; i++) {
		item = transaction.items[i];
		_gaq.push([tracker_prefix+'_addItem',
			transaction.order_num, // order ID - required
			item.sku,       // SKU/code - required
			item.name,      // product name
			item.category,  // category or variation
			item.price,     // unit price - required
			item.quantity   // quantity - required
		]);
	}

	/* Submit Transaction to GA Servers
	.*-----------------------------------------*/
	_gaq.push([tracker_prefix+'_trackTrans']);
};

Tracker.prototype.ua_transaction = function() {
	if (this.is_ec) { return; }  // Prevent double-counted transaction. If EC is enabled, EC txn will be used instead.
	let tracker_prefix, transaction, item, i, j;

	/* Set up the Tracker Prefix
	.*-----------------------------------------*/
	tracker_prefix = this.get_tracker_prefix();
	transaction = tracking.transaction;
	if(!transaction) { return; }

	/* Build Transaction
	.*-----------------------------------------*/
	ga(tracker_prefix+'require', 'ecommerce', 'ecommerce.js');
	ga(tracker_prefix+'ecommerce:addTransaction', {
		'id': transaction.order_number,   // Transaction ID. Required.
		'affiliation': '',                // Affiliation or store name.
		'revenue': transaction.total,     // Grand Total.
		'shipping': transaction.shipping, // Shipping.
		'tax': transaction.tax            // Tax.
	});

	/* Loop through items and add them to the transaction
	.*-----------------------------------------*/
	for(i = 0, j = tracking.products.length; i < j; i++) {
		item = tracking.products[i];
		ga(tracker_prefix+'ecommerce:addItem', {
			'id': transaction.order_number, // Transaction ID. Required.
			'name': item.title,             // Product name. Required.
			'sku': item.product_id,         // SKU/code.
			'category': '',                 // Category or variation.
			'price': item.price,            // Unit price.
			'quantity': item.quantity       // Quantity.
		});
	}

	/* Submit Transaction to GA Servers
	.*-----------------------------------------*/
	ga(tracker_prefix+'ecommerce:send');
};

Tracker.prototype.track_ec_impressions = function() {
	if (this.type === 'rp') { return; }

	const tracker_prefix = this.get_tracker_prefix();

	tracking.ec.products.forEach((product) => {
		ga(tracker_prefix + 'ec:addImpression', product);
	});

	debug('track_ec_impressions', tracking.ec.products);
};

Tracker.prototype.track_ec_product_details = function() {
	if (this.type === 'rp') { return; }

	const tracker_prefix = this.get_tracker_prefix();

	ga(`${tracker_prefix}ec:addProduct`, tracking.ec.product_detail);
	ga(`${tracker_prefix}ec:setAction`, 'detail');
	debug('track_ec_product_details', tracking.ec.product_detail);
};

Tracker.prototype.track_ec_product_click = function() {
	if (this.type === 'rp') { return; }

	const tracker_prefix = this.get_tracker_prefix();

	const list = get(tracking, 'ec.product_click.list');
	const position = get(tracking, 'ec.product_click.position');
	if (list && position) {
		const details = merge({ position }, tracking.ec.product_detail);

		// Set up the product and click action
		ga(`${tracker_prefix}ec:addProduct`, details);
		ga(`${tracker_prefix}ec:setAction`, 'click', { list });

		// Send the click event
		const event_args = ['EnhancedEcommerce', 'TrackProductClick', 'ProductClick'];
		this.track_event(event_args, { nonInteraction: true });

		debug('track_ec_product_click', tracking.ec.product_click);
	}
};

Tracker.prototype.track_ec_cart_event = function(eventName, product_details) {
	if (this.type === 'rp') { return; }

	const products = isArray(product_details) ? product_details : [product_details];

	const tracker_prefix = this.get_tracker_prefix();

	products.forEach((product) => {
		ga(`${tracker_prefix}ec:addProduct`, product);
	});
	ga(`${tracker_prefix}ec:setAction`, eventName);  // eventName should be 'add' or 'remove'

	// Send the cart event
	const event_args = ['EnhancedEcommerce', 'TrackCartEvent', `cart_${eventName}`];
	this.track_event(event_args, { nonInteraction: true });

	debug('track_ec_cart_event', eventName, products);
};

Tracker.prototype.track_ec_complete_transaction = function() {
	if (this.type === 'rp') { return; }

	const tracker_prefix = this.get_tracker_prefix();

	tracking.ec.completed_cart_products.forEach((product) => {
		ga(`${tracker_prefix}ec:addProduct`, product);
	});
	ga(`${tracker_prefix}ec:setAction`, 'purchase', tracking.ec.transaction_details);

	debug('track_ec_complete_transaction', tracking.ec.completed_cart_products, tracking.ec.transaction_details);
};

Tracker.prototype.do_ec_product_tracking = function() {
	// If tracking.ec.product_detail is in use, we should be on a product page and so we need to send product details
	if (!isEmpty(tracking.ec.product_detail)) {
		// If tracking.ec.product_click is in use, we should also track the click that got us to the product page
		// NOTE: We want to catch this first so we can send out the click event separately from the details event
		if (!isEmpty(tracking.ec.product_click)) {
			this.track_ec_product_click();
		}

		this.track_ec_product_details();
	}

	// If tracking.ec.products is in use, that means impressions should have occurred for each of those products
	if (!isEmpty(tracking.ec.products)) {
		this.track_ec_impressions();
	}

	// Construct a variety of checks so we are extra-certain we want to do the final purchase analytics
	const transaction_id = get(tracking, 'ec.transaction_details.id');
	const storage_key = `analytics:${this.get_tracker_prefix()}:order_completed:${transaction_id}`;
	const has_tracking_data = (!isEmpty(get(tracking, 'ec.completed_cart_products'))
		&& !isEmpty(get(tracking, 'ec.transaction_details')));
	const is_completed_page = (tracking.page === 'checkout/completed');
	const analytics_completed = (LocalStorage.getItem(storage_key) !== null);
	const track_transaction = get(tracking, 'transaction.track_transaction');
	const should_do_order_completed_analytics = has_tracking_data
		&& is_completed_page
		&& !analytics_completed
		&& track_transaction;

	debug('Pre-purchase analytics checks', {
		has_tracking_data,
		is_completed_page,
		analytics_completed,
		track_transaction,
		should_do_order_completed_analytics,
		storage_key,
	});

	// If all of the checks pass, fire the relevant analytics function
	if (should_do_order_completed_analytics) {
		// KT-2536: Adding some logs here so we can get more information if duplicate order tracking happens
		rp_app.logger.error({
			message: `ORDER_ANALYTICS_TRACKING id: ${transaction_id}`,
			total_quantity: get(tracking, 'transaction.total_quantity'),
			transaction_id: transaction_id,
		});

		this.track_ec_complete_transaction();
		LocalStorage.setItem(storage_key, true);
	}
};

module.exports = Tracker;
